<script setup lang="ts">
import { onMounted } from "vue";
import { useRoute } from "vue-router";
import { useFirebaseAuth } from "vuefire";
import { useAccessStore } from '@/stores/access';
import { LogCode, useComponentLogger } from "@/logger";
import { getNlaUsers } from "@/stores/utils.ts";
import { usePropValidator } from "@/composables/usePropValidator.ts";
import { createCorrelationId } from '@/lib/correlationId';

const props = defineProps<{
  articleIds: string,
  mmoId: string,
  redirectUrl: string
}>();

const accessStore = useAccessStore();

const logger = useComponentLogger();
const _user = useFirebaseAuth()!.currentUser;
const route = useRoute();
// Local to this component: inherit the ID from route.query.correlationId
// (set at sendEmail() and carried through the magic link). Only mint a new
// one if this page was reached without that query param at all.
const correlationId = typeof route.query.correlationId === "string"
  ? route.query.correlationId
  : createCorrelationId();

const {
  hasMandatoryParams,
  validationErrorMessage
} = usePropValidator(props, [
  'articleIds',
  'mmoId',
  'redirectUrl'
]);

onMounted(async () => {
  if (hasMandatoryParams.value) {
    await accessStore.fetchAccess(props.articleIds, props.mmoId, props.redirectUrl, correlationId);
  } else {
    const nlaUsers = await getNlaUsers(_user!);
    const nlaUserIds = nlaUsers?.map(user => user.id) ?? [];
    logger.warn('Access Check: ' + validationErrorMessage.value, {
      logCode: LogCode.ACCESS_CHECK_MISSING_PARAMETERS,
      dataFields: {
        userIds: nlaUserIds,
        articleIds: props.articleIds || '[MISSING]',
        mmoId: props.mmoId || '[MISSING]',
      },
      redirectUrl: props.redirectUrl || '[MISSING]',
      correlationId,
    });
  }
});

</script>

<template>
  <div class="card mx-auto">
    <div class="card-body">
      <div class="card-text">
        <div v-if="validationErrorMessage" class="error-message">
          {{ `${validationErrorMessage}\n\nReference ID: ${correlationId}` }}
        </div>

        <div v-if="accessStore.error && accessStore.isFinished" class="error-message">
          {{ accessStore.error }}
        </div>

        <div v-if="!accessStore.error && accessStore.isFinished" class="alert alert-info">
          Redirecting
        </div>
        <div v-if="hasMandatoryParams && !accessStore.isFinished">
          <span class="loader" />
          <p>Authorising</p>
        </div>
      </div>
    </div>
  </div>
</template>
